import { businessMinutesBetween, BusinessWindow, lisbonDateParts, lisbonWallClock, utcWallClock } from "../../../dashboard-framework/businessMinutes";
import { monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";
import { paletteColorAt } from "../../../dashboard-framework/colors";
import { buildPivot, PivotMeasure } from "../../../dashboard-framework/pivot";
import { TREATMENT_STATUS_LABELS_PT } from "../../../dashboard-framework/describeWidgetLogic";
import { attendanceRatePct } from "../shared/sharedMetrics";
import { LEADS_WITHOUT_STATUS_LABEL, SESSION_DONE_STATUS } from "./receptionKpisConfig";

type StatusCountRow = {
    status: string;
    count: string | number;
};

export const attendanceRateFromStatusRows = (rows: Array<StatusCountRow>) => {
    const countForStatus = (status: string) => Number(rows.find((row) => row.status === status)?.count || 0);
    const done = countForStatus("done");
    const cancelled = countForStatus("cancelled");
    const missed = countForStatus("missed");
    const denominator = done + cancelled + missed;
    const rawRatePct = attendanceRatePct(done, cancelled, missed);
    const ratePct = rawRatePct === null ? null : Math.round(rawRatePct * 10) / 10;
    const result = { ratePct, done, denominator };
    return result;
};

export type ContactLeadItem = {
    id: number;
    date_created: string;
    first_contact_attempt: string | null;
};

export const leadBusinessMinutes = (lead: ContactLeadItem, windows: Array<BusinessWindow>) => {
    if (!lead.first_contact_attempt) return null;
    const minutes = businessMinutesBetween(lisbonWallClock(lead.date_created), utcWallClock(lead.first_contact_attempt), windows);
    return minutes;
};

export const averageBusinessMinutes = (leads: Array<ContactLeadItem>, windows: Array<BusinessWindow>) => {
    const allMinutes = leads
        .map((lead) => leadBusinessMinutes(lead, windows))
        .filter((minutes): minutes is number => minutes !== null);
    if (allMinutes.length === 0) return null;
    const average = allMinutes.reduce((sum, minutes) => sum + minutes, 0) / allMinutes.length;
    const rounded = Math.round(average * 100) / 100;
    return rounded;
};

type MonthlyEvaluationItem = {
    client: { lead: { date_created: string } | null } | null;
};

export const leadsEfficiencyPoints = (
    leadCountsByMonthIndex: Map<number, number>,
    evaluationItems: Array<MonthlyEvaluationItem>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    const evaluationsByMonthIndex = new Map<number, number>();
    evaluationItems.forEach((item) => {
        const leadCreated = item.client?.lead?.date_created;
        if (!leadCreated) return;
        const monthIndex = Number(leadCreated.slice(0, 4)) * 12 + (Number(leadCreated.slice(5, 7)) - 1);
        evaluationsByMonthIndex.set(monthIndex, (evaluationsByMonthIndex.get(monthIndex) || 0) + 1);
    });

    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const leads = leadCountsByMonthIndex.get(monthIndex) || 0;
        const evaluations = evaluationsByMonthIndex.get(monthIndex) || 0;
        const efficiencyPct = leads > 0 ? Math.round((evaluations / leads) * 10000) / 100 : 0;
        points.push({ monthLabel: monthLabelFromIndex(monthIndex), efficiencyPct });
    }
    return points;
};

type RawWaitingTreatment = {
    id: number;
    date_created: string;
    treatment_status_new: string;
    client: { client_id: string | null } | null;
};

export type WaitingTreatmentItem = {
    id: number;
    clientNumber: string;
    statusLabel: string;
    monthIndex: number;
};

const monthIndexOf = (isoTimestamp: string) => Number(isoTimestamp.slice(0, 4)) * 12 + (Number(isoTimestamp.slice(5, 7)) - 1);

export const waitingTreatmentItems = (treatments: Array<RawWaitingTreatment>): Array<WaitingTreatmentItem> => {
    const items = treatments.map((treatment) => {
        const item = {
            id: treatment.id,
            clientNumber: treatment.client?.client_id || `#${treatment.id}`,
            statusLabel: TREATMENT_STATUS_LABELS_PT[treatment.treatment_status_new] || treatment.treatment_status_new,
            monthIndex: monthIndexOf(treatment.date_created),
        };
        return item;
    });
    return items;
};

const waitingTreatmentsMeasures: Array<PivotMeasure<WaitingTreatmentItem>> = [
    { key: "treatments", label: "Tratamentos", compute: (cellItems) => cellItems.length, format: (value) => String(value ?? 0) },
];

// Pivot expansível estado → mês de criação → cliente (folha liga ao tratamento), réplica do
// pivot Zoho "Estado tratamentos leads"; estados ordenados por contagem desc, meses cronológicos.
export const waitingTreatmentsPivot = (items: Array<WaitingTreatmentItem>) => {
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => item.statusLabel, (item) => monthLabelFromIndex(item.monthIndex), (item) => String(item.id)],
        measures: waitingTreatmentsMeasures,
        sortRowsBy: [
            (firstNode, secondNode) => Number(secondNode.rawByColumn.treatments || 0) - Number(firstNode.rawByColumn.treatments || 0),
            (firstNode, secondNode) => (firstNode.items[0]?.monthIndex || 0) - (secondNode.items[0]?.monthIndex || 0),
            (firstNode, secondNode) => secondNode.items[0].id - firstNode.items[0].id,
        ],
    });
    return pivot;
};

// Filtro do limiar de minutos úteis da análise detalhada: null = todas; com limiar, mantém as leads
// com minutos ESTRITAMENTE acima E as ainda sem 1.ª tentativa de contacto (minutos null) — essas são
// os piores casos (ninguém ligou) e esconder-se-iam exatamente quando mais interessam.
export const contactLeadsAboveThreshold = (leads: Array<ContactLeadItem>, windows: Array<BusinessWindow>, minMinutes: number | null) => {
    if (minMinutes === null) return leads;
    const filteredLeads = leads.filter((lead) => {
        const minutes = leadBusinessMinutes(lead, windows);
        const keepLead = minutes === null || minutes > minMinutes;
        return keepLead;
    });
    return filteredLeads;
};

export const contactDetailRows = (leads: Array<ContactLeadItem>, windows: Array<BusinessWindow>, linkPrefix: string) => {
    const formatTimestampPt = (isoTimestamp: string | null) => {
        if (!isoTimestamp) return "—";
        const formatted = `${isoTimestamp.slice(8, 10)}/${isoTimestamp.slice(5, 7)}/${isoTimestamp.slice(0, 4)} ${isoTimestamp.slice(11, 16)}`;
        return formatted;
    };

    const rows = leads.map((lead) => {
        const minutes = leadBusinessMinutes(lead, windows);
        const row = {
            link: `${linkPrefix}${lead.id}`,
            created: formatTimestampPt(lead.date_created),
            firstContact: formatTimestampPt(lead.first_contact_attempt),
            minutes: minutes === null ? "—" : minutes.toFixed(2),
        };
        return row;
    });
    return rows;
};

// === Produtividade de receção por pessoa (réplica do Zoho "Dashboard Maria") ===

export const RECEPTION_UNKNOWN_PERSON_KEY = "desconhecido";
export const RECEPTION_UNKNOWN_PERSON_NAME = "Desconhecido";

const personFullName = (firstName?: string | null, lastName?: string | null) => {
    const fullName = [firstName, lastName].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    return fullName;
};

const nameForPersonKey = (personKey: string, nameById: Record<string, string>) => {
    if (personKey === RECEPTION_UNKNOWN_PERSON_KEY) return RECEPTION_UNKNOWN_PERSON_NAME;
    const name = nameById[personKey] || RECEPTION_UNKNOWN_PERSON_NAME;
    return name;
};

const roundTo = (value: number, decimals: number) => {
    const factor = 10 ** decimals;
    const rounded = Math.round(value * factor) / factor;
    return rounded;
};

type BookingStatusRow = {
    user_created: string | null;
    status: string;
    count: string | number;
};

export type PersonBookingRow = {
    personKey: string;
    fullName: string;
    marcadas: number;
    realizadas: number;
    comparenciaPct: number | null;
};

// Marcações de 1.ª EEG por pessoa que as criou: marcadas (todas) e realizadas (done).
// % = realizadas ÷ marcadas (fórmula Zoho — NÃO é a taxa de comparência do funil). Ordena por nome.
export const perPersonBookingRows = (rows: Array<BookingStatusRow>, nameById: Record<string, string>) => {
    const marcadasByKey = new Map<string, number>();
    const realizadasByKey = new Map<string, number>();
    rows.forEach((row) => {
        const personKey = row.user_created || RECEPTION_UNKNOWN_PERSON_KEY;
        const count = Number(row.count) || 0;
        marcadasByKey.set(personKey, (marcadasByKey.get(personKey) || 0) + count);
        if (row.status === SESSION_DONE_STATUS) realizadasByKey.set(personKey, (realizadasByKey.get(personKey) || 0) + count);
    });
    const bookingRows = Array.from(marcadasByKey.entries()).map(([personKey, marcadas]) => {
        const realizadas = realizadasByKey.get(personKey) || 0;
        const bookingRow = {
            personKey,
            fullName: nameForPersonKey(personKey, nameById),
            marcadas,
            realizadas,
            comparenciaPct: marcadas > 0 ? roundTo((realizadas / marcadas) * 100, 1) : null,
        };
        return bookingRow;
    });
    bookingRows.sort((firstRow, secondRow) => firstRow.fullName.localeCompare(secondRow.fullName));
    return bookingRows;
};

export const marcadasByPersonKey = (bookingRows: Array<PersonBookingRow>) => {
    const byKey = new Map(bookingRows.map((row) => [row.personKey, row.marcadas]));
    return byKey;
};

type CallOperatorAggRow = {
    user_created: string | null;
    count: string | number;
    sum?: { call_time_seconds: string | number | null } | null;
};

export type PersonCallRow = {
    personKey: string;
    fullName: string;
    chamadas: number;
    totalMinutos: number;
    marcacoes: number;
    chamadasPorMarcacao: number | null;
    minutosPorMarcacao: number | null;
};

// Chamadas e minutos por operador, cruzados com as marcações de 1.ª EEG dessa pessoa. Universo =
// união de quem fez chamadas e de quem marcou. Rácios só quando há chamadas E marcações (senão null).
export const perPersonCallRows = (
    callRows: Array<CallOperatorAggRow>,
    marcadasByKey: Map<string, number>,
    nameById: Record<string, string>
) => {
    const chamadasByKey = new Map<string, number>();
    const secondsByKey = new Map<string, number>();
    callRows.forEach((row) => {
        const personKey = row.user_created || RECEPTION_UNKNOWN_PERSON_KEY;
        chamadasByKey.set(personKey, (chamadasByKey.get(personKey) || 0) + (Number(row.count) || 0));
        secondsByKey.set(personKey, (secondsByKey.get(personKey) || 0) + (Number(row.sum?.call_time_seconds) || 0));
    });
    const personKeys = Array.from(new Set([...chamadasByKey.keys(), ...marcadasByKey.keys()]));
    const callRowsPerPerson = personKeys.map((personKey) => {
        const chamadas = chamadasByKey.get(personKey) || 0;
        const totalMinutos = roundTo((secondsByKey.get(personKey) || 0) / 60, 2);
        const marcacoes = marcadasByKey.get(personKey) || 0;
        const hasRatio = chamadas > 0 && marcacoes > 0;
        const callRow = {
            personKey,
            fullName: nameForPersonKey(personKey, nameById),
            chamadas,
            totalMinutos,
            marcacoes,
            chamadasPorMarcacao: hasRatio ? roundTo(chamadas / marcacoes, 2) : null,
            minutosPorMarcacao: hasRatio ? roundTo(totalMinutos / marcacoes, 2) : null,
        };
        return callRow;
    });
    callRowsPerPerson.sort((firstRow, secondRow) => firstRow.fullName.localeCompare(secondRow.fullName));
    return callRowsPerPerson;
};

export const bookingRowsTotal = (rows: Array<PersonBookingRow>) => {
    const marcadas = rows.reduce((sum, row) => sum + row.marcadas, 0);
    const realizadas = rows.reduce((sum, row) => sum + row.realizadas, 0);
    const totals = { marcadas, realizadas, comparenciaPct: marcadas > 0 ? roundTo((realizadas / marcadas) * 100, 1) : null };
    return totals;
};

export const callRowsTotal = (rows: Array<PersonCallRow>) => {
    const chamadas = rows.reduce((sum, row) => sum + row.chamadas, 0);
    const totalMinutos = roundTo(rows.reduce((sum, row) => sum + row.totalMinutos, 0), 2);
    const marcacoes = rows.reduce((sum, row) => sum + row.marcacoes, 0);
    const hasRatio = chamadas > 0 && marcacoes > 0;
    const totals = {
        chamadas,
        totalMinutos,
        marcacoes,
        chamadasPorMarcacao: hasRatio ? roundTo(chamadas / marcacoes, 2) : null,
        minutosPorMarcacao: hasRatio ? roundTo(totalMinutos / marcacoes, 2) : null,
    };
    return totals;
};

type RawCallForChart = {
    date_created: string;
    call_time_seconds: number | string | null;
    user_created: { id: string; first_name?: string | null; last_name?: string | null } | null;
};

export type ConversationDay = {
    dayKey: string;
    label: string;
};

const lisbonDayKeyOf = (isoTimestamp: string) => {
    const parts = lisbonDateParts(isoTimestamp);
    const dayKey = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
    return dayKey;
};

type RawSchedule = {
    start_date: string;
    end_date: string;
    notes?: string | null;
    user: { id: string; first_name?: string | null; last_name?: string | null } | string | null;
};

const scheduleUserId = (schedule: RawSchedule) => {
    const userId = typeof schedule.user === "string" ? schedule.user : schedule.user?.id || null;
    return userId;
};

const scheduleHours = (schedule: RawSchedule) => {
    const milliseconds = new Date(schedule.end_date).getTime() - new Date(schedule.start_date).getTime();
    const hours = milliseconds > 0 ? milliseconds / 3_600_000 : 0;
    return hours;
};

export type ScheduleCallsPerson = { key: string; label: string; color: string };

// Por pessoa × dia (hora de Lisboa): a barra = horas agendadas; o sub-segmento sólido = horas de
// chamada (o resto da barra = restante do turno). Universo = operadores com chamadas ∪ equipa de
// receção (roster) com horário, ambos com dados na janela; chamadas sem utilizador são ignoradas.
// Notas do horário agregadas por pessoa/dia para o tooltip. Cada pessoa tem a sua própria stack
// (lado a lado no dia). Cor estável por índice (ordem alfabética).
export const scheduleCallsByPersonDayPoints = (
    calls: Array<RawCallForChart>,
    schedules: Array<RawSchedule>,
    teamUserIds: Array<string>,
    days: Array<ConversationDay>
) => {
    const dayKeys = new Set(days.map((day) => day.dayKey));
    const teamSet = new Set(teamUserIds);
    const labelByPerson = new Map<string, string>();
    const callOperatorKeys = new Set<string>();
    const callSecondsByDayPerson = new Map<string, Map<string, number>>();
    const schedHoursByDayPerson = new Map<string, Map<string, number>>();
    const notesByDayPerson = new Map<string, Map<string, Array<string>>>();
    const relevantPersons = new Set<string>();

    calls.forEach((call) => {
        const dayKey = lisbonDayKeyOf(call.date_created);
        if (!dayKeys.has(dayKey) || !call.user_created) return;
        const personKey = call.user_created.id;
        labelByPerson.set(personKey, personFullName(call.user_created.first_name, call.user_created.last_name) || RECEPTION_UNKNOWN_PERSON_NAME);
        callOperatorKeys.add(personKey);
        relevantPersons.add(personKey);
        const dayMap = callSecondsByDayPerson.get(dayKey) || new Map<string, number>();
        dayMap.set(personKey, (dayMap.get(personKey) || 0) + (Number(call.call_time_seconds) || 0));
        callSecondsByDayPerson.set(dayKey, dayMap);
    });

    schedules.forEach((schedule) => {
        const personKey = scheduleUserId(schedule);
        if (!personKey || !(teamSet.has(personKey) || callOperatorKeys.has(personKey))) return;
        const dayKey = lisbonDayKeyOf(schedule.start_date);
        if (!dayKeys.has(dayKey)) return;
        if (typeof schedule.user !== "string" && schedule.user) {
            const label = personFullName(schedule.user.first_name, schedule.user.last_name);
            if (label) labelByPerson.set(personKey, label);
        }
        relevantPersons.add(personKey);
        const dayMap = schedHoursByDayPerson.get(dayKey) || new Map<string, number>();
        dayMap.set(personKey, (dayMap.get(personKey) || 0) + scheduleHours(schedule));
        schedHoursByDayPerson.set(dayKey, dayMap);
        const note = (schedule.notes || "").trim();
        if (note) {
            const noteDayMap = notesByDayPerson.get(dayKey) || new Map<string, Array<string>>();
            const personNotes = noteDayMap.get(personKey) || [];
            personNotes.push(note);
            noteDayMap.set(personKey, personNotes);
            notesByDayPerson.set(dayKey, noteDayMap);
        }
    });

    const personKeys = Array.from(relevantPersons).sort((firstKey, secondKey) =>
        (labelByPerson.get(firstKey) || "").localeCompare(labelByPerson.get(secondKey) || "")
    );
    const persons: Array<ScheduleCallsPerson> = personKeys.map((personKey, index) => {
        const person = { key: personKey, label: labelByPerson.get(personKey) || RECEPTION_UNKNOWN_PERSON_NAME, color: paletteColorAt(index) };
        return person;
    });

    const points = days.map((day) => {
        const point: Record<string, string | number> = { day: day.label };
        personKeys.forEach((personKey) => {
            const callHours = roundTo((callSecondsByDayPerson.get(day.dayKey)?.get(personKey) || 0) / 3600, 2);
            const schedHours = roundTo(schedHoursByDayPerson.get(day.dayKey)?.get(personKey) || 0, 2);
            point[`${personKey}__calls`] = callHours;
            point[`${personKey}__sched`] = schedHours;
            point[`${personKey}__rest`] = roundTo(Math.max(0, schedHours - callHours), 2);
            point[`${personKey}__note`] = (notesByDayPerson.get(day.dayKey)?.get(personKey) || []).join(" · ");
        });
        return point;
    });

    const result = { persons, points };
    return result;
};

type CallDayAggRow = {
    user_created: string | null;
    sum?: { call_time_seconds: string | number | null } | null;
};

export type AvgConversationRow = { key: string; label: string; avgHours: number };

// Média de horas de conversação por dia com chamadas, por pessoa, no período (gráfico só gestão).
// Cada linha do agregado (groupBy user_created + dia) é um dia ativo → média = (Σ s ÷ 3600) ÷ nº dias.
// Ordena por média desc; chamadas sem utilizador ignoradas.
export const avgDailyConversationByPerson = (rows: Array<CallDayAggRow>, nameById: Record<string, string>) => {
    const byPerson = new Map<string, { seconds: number; activeDays: number }>();
    rows.forEach((row) => {
        const personKey = row.user_created;
        if (!personKey) return;
        const bucket = byPerson.get(personKey) || { seconds: 0, activeDays: 0 };
        bucket.seconds += Number(row.sum?.call_time_seconds) || 0;
        bucket.activeDays += 1;
        byPerson.set(personKey, bucket);
    });
    const avgRows = Array.from(byPerson.entries()).map(([personKey, bucket]) => {
        const avgRow = {
            key: personKey,
            label: nameById[personKey] || RECEPTION_UNKNOWN_PERSON_NAME,
            avgHours: bucket.activeDays > 0 ? roundTo(bucket.seconds / 3600 / bucket.activeDays, 2) : 0,
        };
        return avgRow;
    });
    avgRows.sort((firstRow, secondRow) => secondRow.avgHours - firstRow.avgHours);
    return avgRows;
};

export type LeadStatusCountRow = {
    status: string | null;
    count: string | number;
};

// Leads por estado (fotografia da gestão do call center). O estado vazio ganha linha própria
// ("Sem estado"): são as leads ainda por triar, exatamente as que interessa não perder de vista.
// Etiqueta pt-PT resolvida pelo caller a partir das choices do campo Directus, para nunca divergir da grid.
export const leadStatusRows = (rows: Array<LeadStatusCountRow>, labelForStatus: (status: string) => string) => {
    const statusRows = rows.map((row) => ({
        statusValue: row.status || null,
        label: row.status ? labelForStatus(row.status) : LEADS_WITHOUT_STATUS_LABEL,
        leads: Number(row.count || 0),
    }));
    const sortedRows = statusRows.sort(
        (firstRow, secondRow) => secondRow.leads - firstRow.leads || firstRow.label.localeCompare(secondRow.label, "pt")
    );
    return sortedRows;
};

// Filtro Directus da seleção de estados. "Sem estado" é um valor sentinela da UI (não existe na BD):
// traduz-se em `status._null`, em _or com o _in — um _in sozinho descarta silenciosamente os nulos.
export const leadStatusSelectionFilter = (selectedValues: Array<string>, withoutStatusValue: string) => {
    const rawStatuses = selectedValues.filter((value) => value !== withoutStatusValue);
    const includesWithoutStatus = selectedValues.includes(withoutStatusValue);
    const clauses: Array<any> = [];
    if (rawStatuses.length > 0) clauses.push({ status: { _in: rawStatuses } });
    if (includesWithoutStatus) clauses.push({ status: { _null: true } });
    if (clauses.length === 0) return null;
    const filter = clauses.length === 1 ? clauses[0] : { _or: clauses };
    return filter;
};
